import { Component, DoCheck, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router, RouterStateSnapshot } from '@angular/router';
import { FormBuilder, Validators } from '@angular/forms';
import { SurveyService } from '../../../services/surveys.service';
import { ToastService } from '../../../services/general/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { MatStepper } from '@angular/material/stepper';
import { UserService } from '../../../services/user.service';
import { HeaderTitleService } from '../../../services/general/header-title.service';
import { isEqual } from 'lodash-es';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';


@Component({
  selector: 'app-survey-response-page',
  templateUrl: './survey-response-page.component.html',
  styleUrls: ['./survey-response-page.component.scss']
})
export class SurveyResponsePageComponent implements OnInit, OnDestroy, DoCheck {
  @Input() surveyID = null;
  @Input() impersonateEmail = null;
  @ViewChild('stepper', { static: false }) stepper: MatStepper;
  userInfoForm = this._formBuilder.group({
    name: ['', Validators.required],
    email: ['', [Validators.required, Validators.email]],
  });

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private _formBuilder: FormBuilder,
    private surveyService: SurveyService,
    public userService: UserService,
    private toast: ToastService,
    private translateService: TranslateService,
    private headerTitleService: HeaderTitleService
  ) {
  }

  loading = false;
  survey_id = null;
  survey = null;
  autoSaveLoading = false;

  answerStatus = null;
  answerTimestamp = null;
  justSubmitted = null;

  private surveySnapshot: any = null;
  private surveyChange$ = new Subject<void>();
  private surveyChangeSub: any;

  ngOnInit(): void {
    this.init();
  }

  init() {
    if (this.surveyID) {
      this.survey_id = this.surveyID;
      this.getSurvey();
      return;
    }
    this.route.params.subscribe(params => {
      this.survey_id = params.survey_id;
      this.getSurvey();
    });
  }


  ngDoCheck() {
    if (this.survey && !isEqual(this.survey, this.surveySnapshot)) {
      this.surveySnapshot = JSON.parse(JSON.stringify(this.survey));
      this.surveyChange$.next();
    }
  }

  ngOnDestroy() {
    if (this.surveyChangeSub) {
      this.surveyChangeSub.unsubscribe();
    }
  }

  getSurvey() {
    this.loading = true;
    const params: any = {};
    if (this.impersonateEmail) {
      params.impersonate_email = this.impersonateEmail;
    }
    this.surveyService.surveyDetailsResponsePage(this.survey_id, params, (result) => {
      if (result?.is_draft) {
        this.router.navigate(['/']);
      }

      if (result.answered) {
        this.impersonateEmail = true;
        this.surveyID = this.survey_id;
      }

      const draft = result?.draft?.customs?.draft;
      this.answerTimestamp = result?.draft?.created_at;
      this.answerStatus = result.draft?.status;
      delete result?.draft;
      this.survey = result;
      if (draft) {
        this.survey = draft;
      }

      if (this.impersonateEmail) {
        this.survey.answered = true;
      }

      if (result.visibility_type === 'PRIVATE') {
        this.userService.getUser(() => {
          this.userService.resetCurrentLanguage();
        });
      }
      if (!this.userService.userInfo) {
        this.survey.user = {
          name: '',
          email: '',
        };

        if (this.survey.customs && this.survey.customs.anagraphic_is_mandatory === false) {
          this.userInfoForm.get('name').clearValidators();
          this.userInfoForm.get('name').updateValueAndValidity();
          this.userInfoForm.get('email').setValidators([Validators.email]);
          this.userInfoForm.get('email').updateValueAndValidity();
        }
      }
      this.headerTitleService.setTitle(result.title);
      this.survey.edit_after_submit = !!this.survey.edit_after_submit;
      this.survey.attachments = result.attachment_files?.concat(result.attachment_pubmeds);

      if (result.visibility_type === 'PRIVATE') {
        setTimeout(() => {
          this.surveyChangeSub = this.surveyChange$
            .pipe(debounceTime(5000))
            .subscribe(() => {
              if (this.survey && !this.autoSaveLoading && !this.survey.answered) {
                this.autoSave();
              }
            });
        }, 5000);
      }

      this.loading = false;
    }, (error) => {
      this.router.navigate(['/']);
      this.loading = false;
    });
  }

  surveyResponseIsValid() {
    const status = this.surveyService.surveyAnswerIsValid(this.survey);
    // @ts-ignore
    if (!status?.valid) {
      // @ts-ignore
      this.toast.error(this.translateService.instant('generic.warning'), this.translateService.instant(status?.message));
      return false;
    }

    if (this.survey.visibility_type === 'PUBLIC' && !this.userService.userInfo) {
      if (this.userInfoForm.invalid) {
        this.toast.error(this.translateService.instant('generic.warning'), this.translateService.instant('swal.errors.field-mandatory'));
        return false;
      }
    }

    return true;
  }

  onSubmit(is_draft = false) {
    const submit = () => {
      this.loading = true;
      this.surveyService.createSurveyAnswer(this.survey_id, this.survey, (result) => {
        if (!is_draft) {
          this.stepper.next();
          this.justSubmitted = true;
          this.survey.answered = true;
        }
        this.toast.success(
          this.translateService.instant('generic.success'),
          this.translateService.instant(is_draft ? 'pages.surveys.response.drafted-successfully' : 'pages.surveys.response.submitted-successfully'),
        );
        this.loading = false;
      }, () => {
        this.loading = false;
      });
    };

    this.survey.is_draft = is_draft;
    if (is_draft) {
      submit();
    } else {
      if (this.surveyResponseIsValid()) {
        submit();
      }
    }
  }

  autoSave() {
    this.survey.is_draft = true;
    this.autoSaveLoading = true;
    this.surveyService.createSurveyAnswer(this.survey_id, this.survey, (result) => {
      this.autoSaveLoading = false;
    }, () => {
      this.autoSaveLoading = false;
    });
  }

  shouldShowAnagraphicAt(position: 'START' | 'END'): boolean {
    if (!this.survey || this.survey.visibility_type !== 'PUBLIC' || this.userService.userInfo) {
      return false;
    }
    const currentPosition = this.survey.customs?.anagraphic_position || 'START';
    return currentPosition === position;
  }

  validateQuestionsAndProceed(stepper: MatStepper) {
    const status = this.surveyService.surveyAnswerIsValid(this.survey);
    // @ts-ignore
    if (!status?.valid) {
      // @ts-ignore
      this.toast.error(this.translateService.instant('generic.warning'), this.translateService.instant(status?.message));
      return;
    }
    stepper.next();
  }
}
